import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CHART_REGISTRY } from '../../config/chartRegistry';
import AnalyticsTabHeroScorecards from './AnalyticsTabHeroScorecards';
import {
  TerminalPricePulseScorecards,
  VolumeByOriginScorecards,
} from './KpiScorecards';
import AnalyticsTabWeatherMapScorecards from './AnalyticsTabWeatherMapScorecards';

/** Single-chart fullscreen: symmetric padding inside overlay. */
const PRESET_SECTION_Y_FULLSCREEN = 'pt-3 pb-3';

/**
 * Every wrapper in the single-chart fullscreen column fills the viewport at
 * `lg+`. Below `lg` the same column is taller than the viewport (KPI cards and
 * legend stack), so this class drops the fill and lets it grow — the overlay
 * body scrolls instead. See the `.preset-fullscreen-*` block in index.css.
 */
const FULLSCREEN_FILL = 'preset-fullscreen-fill';

const PRESET_VIEW_WIDGET_IDS = {
  view1: [
    // Preset View 1 order
    // FOB → Volume-by-Origin → Terminal Market Price Pulse → Smart Price Tracker
    'price-line',
    'stacked-bar',
    'terminal-price-line',
    'cluster',
  ],
  view2: [
    // View 2 — mirror View 1 chart behavior, with AnalyticsTab section order.
    'price-line',
    'terminal-price-line',
    'stacked-bar',
    'volume-line', // Commodity Planner
    'fob-price-avg', // Avg FOB Year
    'price-classifier',
    'cluster',
    'producing-regions', // Global Produce map
    'cargo-varieties', // Cargo shipments (closest match in app registry)
  ],
  custom: [
    // Custom view uses the widget dashboard, not this preset renderer.
  ],
};

/** Preset white card: tall charts get a floor; others size to their content. */
const PRESET_CARD_NO_MIN_HEIGHT_IDS = new Set([
  'producing-regions',
  'fob-price-avg',
  'cargo-varieties',
  'cluster',
]);

function presetChartCardMinClass(chartId) {
  // `preset-chart-card` is the 820px floor; it collapses to content height on
  // phones (< 480px). See index.css.
  const min = PRESET_CARD_NO_MIN_HEIGHT_IDS.has(chartId) ? '' : 'preset-chart-card';
  return [
    min,
    // Single owner of the card's bottom space (24px < lg, 28px lg+): chart
    // bodies, legends and plots add no bottom padding of their own.
    'pb-6',
    'lg:pb-7',
    'rounded-[8px]',
    'border',
    'border-[#E2E2E2]',
    'dark:border-[#3a3c3b]',
    'bg-white',
    'dark:bg-[#282a29]',
  ]
    .filter(Boolean)
    .join(' ');
}

/** View 2: producing map + cargo shipments ignore global From/To (API uses its own reference). */
function apiParamsForPresetChart(apiParams, presetView, chartId) {
  if (presetView !== 'view2' || !apiParams) return apiParams;
  if (chartId === 'producing-regions' || chartId === 'cargo-varieties') {
    return { ...apiParams, dateStart: null, dateEnd: null };
  }
  return apiParams;
}

const ChartSection = ({ children, isDashboardFullscreen, isSingleChartFullscreen }) => {
  return (
    <section
      className={
        isSingleChartFullscreen ? `w-full h-full min-h-0 flex flex-col ${FULLSCREEN_FILL}` : 'w-full'
      }
      style={isSingleChartFullscreen ? undefined : { scrollMarginTop: 92 }}
    >
      <div
        className={
          isSingleChartFullscreen
            ? `w-full h-full min-h-0 flex flex-col flex-1 ${FULLSCREEN_FILL}`
            : isDashboardFullscreen
              ? 'w-full mx-auto'
              : 'w-full'
        }
      >
        {children}
      </div>
    </section>
  );
};

/**
 * Locked, preset "Standard" Analytics view.
 * - No add/remove/move widgets
 * - One chart centered per scroll section
 * - chartFullscreenId: when set, only that chart is rendered (viewport overlay from parent)
 */
const PresetAnalyticsStandard = ({
  apiParams,
  isDashboardFullscreen = false,
  presetView = 'view1',
  chartFullscreenId = null,
  onEnterChartFullscreen,
  onExitChartFullscreen,
}) => {
  const { t } = useTranslation();

  const charts = useMemo(() => {
    const ids = PRESET_VIEW_WIDGET_IDS[presetView] || PRESET_VIEW_WIDGET_IDS.view1;
    const list = ids.map((id) => CHART_REGISTRY[id]).filter(Boolean);
    if (!chartFullscreenId) return list;
    return list.filter((c) => c.id === chartFullscreenId);
  }, [chartFullscreenId, presetView]);

  const isSingleChartFullscreen = !!chartFullscreenId;
  const showTopScorecards = presetView === 'view2' && !isSingleChartFullscreen;
  const isView1Preset = presetView === 'view1';
  const isView2Preset = presetView === 'view2';

  const view2ScorecardsForChart = (chartId) => {
    if (chartId === 'price-line') {
      // Scorecards render inside PriceLineChart so Range spread matches chart filters + dates.
      return null;
    }
    if (chartId === 'terminal-price-line') {
      return <TerminalPricePulseScorecards apiParams={apiParams} dims={{}} gapDims={{}} />;
    }
    if (chartId === 'stacked-bar') {
      return <VolumeByOriginScorecards apiParams={apiParams} dims={{}} />;
    }
    if (chartId === 'producing-regions') {
      return (
        <AnalyticsTabWeatherMapScorecards
          apiParams={apiParamsForPresetChart(apiParams, presetView, chartId)}
        />
      );
    }
    return null;
  };

  const renderPresetChart = (chart, { presetScorecards = null, hideTitle = false, hideHelpButton = false } = {}) => {
    const ChartContainer = chart.containerComponent;
    const hideKpis = chart.id === 'price-line' || chart.id === 'terminal-price-line' || chart.id === 'stacked-bar';
    const chartApiParams = apiParamsForPresetChart(apiParams, presetView, chart.id);
    const fitXToWidth = presetView === 'view2' && (chart.id === 'price-line' || chart.id === 'terminal-price-line');
    return (
      <ChartContainer
        apiParams={chartApiParams}
        forceFiltersOpen={true}
        presetViewId="view2"
        fitXToWidth={fitXToWidth}
        presetScorecards={presetScorecards}
        hidePresetScorecards={hideKpis && !!presetScorecards}
        hideTitle={hideTitle}
        hideHelpButton={hideHelpButton}
        presetDescription={t(chart.descriptionKey)}
        presetSingleChartFullscreen={false}
        hideLegend={
          chart.id === 'price-line' ||
          chart.id === 'terminal-price-line' ||
          chart.id === 'stacked-bar' ||
          chart.id === 'volume-line'
        }
        analyticsFullscreen={{
          isActive: chartFullscreenId === chart.id,
          onEnter: () => onEnterChartFullscreen?.(chart.id),
          onExit: onExitChartFullscreen,
        }}
      />
    );
  };

  return (
    <div
      className={
        isSingleChartFullscreen
          ? `h-full min-h-0 w-full flex flex-col flex-1 ${FULLSCREEN_FILL}`
          : isDashboardFullscreen
            ? 'w-full'
            : 'w-full'
      }
    >
      <div
        className={
          isSingleChartFullscreen
            ? `w-full h-full min-h-0 flex flex-col flex-1 ${FULLSCREEN_FILL}`
            : 'flex w-full flex-col gap-4 lg:gap-6'
        }
      >
        {showTopScorecards ? (
          <ChartSection isDashboardFullscreen={isDashboardFullscreen} isSingleChartFullscreen={false}>
            <div className="w-full">
              <AnalyticsTabHeroScorecards apiParams={apiParams} />
            </div>
          </ChartSection>
        ) : null}

        {isView2Preset && !isSingleChartFullscreen
          ? (() => {
            const idxClassifier = charts.findIndex((c) => c.id === 'price-classifier');
            const idxTracker = charts.findIndex((c) => c.id === 'cluster');

            return charts.map((chart, idx) => {
              // Render classifier + tracker as a single 2-up row, in-place at classifier position.
              if (idx === idxClassifier && idxClassifier !== -1 && idxTracker !== -1) {
                const classifier = charts[idxClassifier];
                const tracker = charts[idxTracker];
                return (
                  <ChartSection
                    key="classifier-tracker-row"
                    isDashboardFullscreen={isDashboardFullscreen}
                    isSingleChartFullscreen={false}
                  >
                    <div className="w-full">
                      <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-stretch gap-6">
                        {[classifier, tracker].map((c) => (
                          <div
                            key={c.id}
                            className="flex min-h-0 flex-col gap-3 lg:h-full"
                          >
                            {view2ScorecardsForChart(c.id) ? (
                              <div className="w-full shrink-0">{view2ScorecardsForChart(c.id)}</div>
                            ) : null}
                            <div
                              className={`${presetChartCardMinClass(c.id)} flex min-h-0 flex-1 flex-col overflow-hidden`}
                            >
                              {c.id === 'producing-regions' ? (
                                <div className="px-4 pt-4">
                                  <h3 className="text-[16px] font-semibold text-[#282A29] dark:text-white">
                                    {t(c.titleKey)}
                                  </h3>
                                </div>
                              ) : null}
                              <div
                                className={
                                  c.id === 'producing-regions'
                                    ? 'preset-chart-plot-map'
                                    : 'flex min-h-0 flex-1 flex-col'
                                }
                              >
                                {renderPresetChart(c, {
                                  presetScorecards: view2ScorecardsForChart(c.id),
                                  hideTitle: c.id === 'producing-regions',
                                  hideHelpButton: true,
                                })}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </ChartSection>
                );
              }
              // Skip tracker here (already rendered in the 2-up row).
              if (idx === idxTracker && idxClassifier !== -1 && idxTracker !== -1) return null;

              const scorecards = view2ScorecardsForChart(chart.id);
              return (
                <ChartSection
                  key={chart.id}
                  isDashboardFullscreen={isDashboardFullscreen}
                  isSingleChartFullscreen={false}
                >
                  <div className="w-full">
                    <div className="flex flex-col gap-3">
                      <div className={presetChartCardMinClass(chart.id)}>
                        {chart.id === 'producing-regions' ? (
                          <div className="px-4 pt-4">
                            <h3 className="text-[16px] font-semibold text-[#282A29] dark:text-white">
                              {t(chart.titleKey)}
                            </h3>
                          </div>
                        ) : null}
                        {chart.id === 'producing-regions' && scorecards ? (
                          <div className="px-4 pb-1 w-full shrink-0">{scorecards}</div>
                        ) : null}
                        <div className={chart.id === 'producing-regions' ? 'preset-chart-plot-map min-h-0' : ''}>
                          {renderPresetChart(chart, {
                            presetScorecards: chart.id === 'producing-regions' ? null : scorecards,
                            hideTitle: chart.id === 'producing-regions',
                            hideHelpButton: true,
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </ChartSection>
              );
            });
          })()
          : charts.map((chart) => {
          const ChartContainer = chart.containerComponent;
          const minHeightClass = isSingleChartFullscreen
            ? `flex-1 min-h-0 flex flex-col ${FULLSCREEN_FILL}`
            : presetChartCardMinClass(chart.id);
          const sectionPadClass = isSingleChartFullscreen
            ? `${PRESET_SECTION_Y_FULLSCREEN} flex-1 min-h-0 flex flex-col ${FULLSCREEN_FILL}`
            : '';
          const fitXToWidth =
            presetView === 'view2' && (chart.id === 'price-line' || chart.id === 'terminal-price-line');
          return (
            <ChartSection
              key={chart.id}
              isDashboardFullscreen={isDashboardFullscreen}
              isSingleChartFullscreen={isSingleChartFullscreen}
            >
              <div className={`w-full ${sectionPadClass}`}>
                <div className={minHeightClass}>
                  <ChartContainer
                    apiParams={apiParamsForPresetChart(apiParams, presetView, chart.id)}
                    forceFiltersOpen
                    presetViewId={isView2Preset ? 'view2' : undefined}
                    fitXToWidth={fitXToWidth}
                    presetDescription={t(chart.descriptionKey)}
                    presetSingleChartFullscreen={isSingleChartFullscreen}
                    hideHelpButton={isView2Preset}
                    hideLegend={
                      (isView1Preset || isView2Preset) &&
                      (chart.id === 'price-line' ||
                        chart.id === 'terminal-price-line' ||
                        (!isSingleChartFullscreen &&
                          (chart.id === 'stacked-bar' || chart.id === 'volume-line')))
                    }
                    analyticsFullscreen={{
                      isActive: chartFullscreenId === chart.id,
                      onEnter: () => onEnterChartFullscreen?.(chart.id),
                      onExit: onExitChartFullscreen,
                    }}
                  />
                </div>
              </div>
            </ChartSection>
          );
        })}
      </div>
    </div>
  );
};

export default PresetAnalyticsStandard;
